import { UIElement } from '../ui-element';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child } from '../../decorators/child.decorator';
import { BrandColor, BrandColors } from '../../global/helpers-marketing';
import { Labels } from '../../global/labels';
import { dispatchCustomEvent } from '../../global/ui-helpers';
import type {
    UIWelcomeLayouts,
    UIWelcomeCsps,
    UIWelcomeLayout,
    UIWelcomeCsp,
} from './ui-welcome.type';
import type { ElementChild, ElementConfig } from '../ui-element.type';
import type { TranslationLabels } from '../../types/labels.type';
import styles from './ui-welcome.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIWelcome
 * @element ui-welcome
 * @classdesc Represents a class for <code>ui-welcome</code> element.
 * IBank specific components which show the info when user has logged in.
 * @property {("vip" | "gold" | "senior")} [csp] {@attr csp} Customer service programme attribute.
 *  {@desc vip}
 *  {@desc gold}
 *  {@desc senior}
 * @property {("primary" | "secondary")} [layout="primary"] {@attr layout} Layout of the element.
 * @property {string} [labelWelcome] {@attr label-welcome} Welcome message.
 * @property {string} [labelHint] {@attr label-hint} Hint text.
 * @property {string} [labelCsp] {@attr label-csp} CSP label for leaf.
 * @property {string} [linkCsp] {@attr link-csp} Link for CSP web-page.
 * @property {HTMLButtonElement} closeAction
 * @fires event:welcome-close
 * @example
 * <ui-welcome
 *     csp="gold"
 *     label-csp="Gold Customer"
 *     label-welcome="Welcome, iBank User!"
 *     label-hint="You previously logged in on: 16.11.2020 14:12:29">
 * </ui-welcome>
 */
@Component({ tag: 'ui-welcome', styles })
export class UIWelcome extends UIElement {
    /**
     * @type {{PRIMARY: string, SECONDARY: string}}
     */
    static readonly layouts: UIWelcomeLayouts = {
        PRIMARY: 'primary',
        SECONDARY: 'secondary',
    } as const;

    /**
     * @type {{VIP: string, GOLD: string, SENIOR: string}}
     */
    static readonly csp: UIWelcomeCsps = {
        VIP: 'vip',
        GOLD: 'gold',
        SENIOR: 'senior',
    } as const;

    @Attribute(String) layout: UIWelcomeLayout = 'primary';
    @Attribute(String) csp?: UIWelcomeCsp;
    @Attribute(String) labelWelcome?: string;
    @Attribute(String) labelHint?: string;
    @Attribute(String) labelCsp?: string;
    @Attribute(String) linkCsp?: string;
    @Child('.ui-welcome__close-action') closeAction?: HTMLButtonElement;

    private closeActionHandler!: EventListener;

    /**
     * @type {Labels}
     * @readonly
     */
    static get labels(): TranslationLabels.Labels {
        return Labels.attach('ui-welcome', {
            close: 'Close',
        });
    }

    private handleCloseAction(): void {
        dispatchCustomEvent(this, 'welcome-close', {});
        this.remove();
    }

    /**
     * Resolves the color of the badge leaf.
     * @returns {UIBrandColor}
     * @private
     */
    private resolveLeafColor(): BrandColor {
        switch (this.csp) {
            case UIWelcome.csp.VIP:
                return BrandColors.Lilac;
            case UIWelcome.csp.GOLD:
                return BrandColors.Orange;
            case UIWelcome.csp.SENIOR:
            default:
                return BrandColors.Turquoise;
        }
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        const color = this.resolveLeafColor();
        const leafConfig =
            this.csp &&
            this.layout === UIWelcome.layouts.PRIMARY &&
            ({
                tagName: 'ui-badge',
                classList: {
                    'ui-welcome__leaf': true,
                },
                attributes: {
                    color: color || null,
                    layout: 'segment',
                },
                children: [this.labelCsp],
            } as ElementConfig);
        const linkScpConfig =
            this.csp &&
            this.layout === UIWelcome.layouts.PRIMARY &&
            ({
                tagName: 'a',
                attributes: {
                    href: this.linkCsp,
                },
                classList: {
                    'ui-welcome__csp-link': true,
                },
                children: [leafConfig],
            } as ElementConfig);
        this.insertElements([
            {
                tagName: 'div',
                classList: {
                    'ui-welcome__container': true,
                },
                children: [
                    {
                        tagName:
                            this.layout === UIWelcome.layouts.SECONDARY
                                ? 'div'
                                : 'h1',
                        classList: {
                            'ui-welcome__label': true,
                        },
                        children: [this.labelWelcome],
                    },
                    {
                        tagName: 'ui-hint',
                        children: [this.labelHint],
                    },
                    this.layout === UIWelcome.layouts.SECONDARY
                        ? {
                              tagName: 'ui-button',
                              classList: {
                                  'ui-welcome__close-action': true,
                              },
                              attributes: {
                                  'type': 'button',
                                  'aria-label': UIWelcome.labels.close,
                                  'title': UIWelcome.labels.close,
                                  'layout': 'tertiary',
                                  'glyph': 'redesign-x-mark',
                              },
                          }
                        : null,
                ] as Array<ElementChild>,
            },
            this.linkCsp
                ? (linkScpConfig as ElementConfig)
                : (leafConfig as ElementConfig),
        ]);
    }

    /**
     * @inheritDoc
     */
    override hydrate(): void {
        this.closeActionHandler = this.handleCloseAction.bind(this);
        this.closeAction?.addEventListener('click', this.closeActionHandler);
    }

    /**
     * @inheritDoc
     */
    override disconnect(): void {
        this.closeAction?.removeEventListener('click', this.closeActionHandler);
    }
}
